import { doc, collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserProfile } from './authService';

/**
 * Listen to user profile for credit changes (updated server-side by Cloud Function).
 * The client no longer writes credits — it just reacts to changes.
 */
export const listenToCredits = (
  userId: string,
  onCreditsChanged: (credits: number) => void
): (() => void) => {
  const userRef = doc(db, `users/${userId}`);

  const unsubscribe = onSnapshot(userRef, (snap) => {
    if (snap.exists()) {
      const data = snap.data() as UserProfile;
      onCreditsChanged(data.credits);
    }
  });

  return unsubscribe;
};

/**
 * Listen for new payment completions under customers/{uid}/payments.
 * Fires callback with the number of credits added when a new succeeded payment arrives.
 */
export const listenToPayments = (
  userId: string,
  onPaymentComplete: (creditsAdded: number) => void
): (() => void) => {
  const paymentsRef = collection(db, `customers/${userId}/payments`);
  let initialLoad = true;

  const unsubscribe = onSnapshot(paymentsRef, (snapshot) => {
    if (initialLoad) {
      initialLoad = false;
      return; // Skip initial load to avoid firing on existing docs
    }

    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const data = change.doc.data();
        if (data.status === 'succeeded') {
          const liters = parseInt(
            data.items?.[0]?.price?.product?.metadata?.liters ||
            data.metadata?.liters ||
            '0',
            10
          );
          if (liters > 0) {
            onPaymentComplete(liters);
          }
        }
      }
    });
  });

  return unsubscribe;
};
